// src/pages/UserMeet/UserMeetRoomPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import "@livekit/components-styles";

import { useSocket } from "@/helpers/useSocket";
import { socketService } from "@/Services/socketService";
import UserMeetStage from "@/components/UserMeet/UserMeetStage";

import { useDeviceCheck } from "../MeetingRoom/hooks/useDeviceCheck";

import {
  joinMeeting,
  leaveMeeting,
  endMeeting,
  getMeetingDetails,
  createBreakout,
  inviteToBreakout,
  respondToBreakoutInvite,
  leaveBreakout,
  endBreakout,
  rejoinBreakout,
  admitParticipant,
  denyParticipant,
} from "@/Services/UserMeetServices";

import {
  BreakoutOverlay,
  CreateBreakoutModal,
  BreakoutInviteModal,
  InviteToBreakoutModal,
  EndBreakoutModal,
  BreakoutActiveModal,
  AdmissionRequestModal,
} from "@/components/UserMeet/User-Meet-Private";

import "@/pages/MeetingRoom/MeetingRoom.css";
import "@/pages/MeetingRoom/ParticipantsPanel.css";

/* ─── Helper: decode JWT to get userId ─── */
function decodeJwtUserId(token) {
  try {
    if (!token || typeof token !== "string") return "";
    const parts = token.split(".");
    if (parts.length !== 3) return "";
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    const payload = JSON.parse(json);
    return (
      payload?._id ||
      payload?.id ||
      payload?.userId ||
      payload?.user_id ||
      payload?.sub ||
      ""
    );
  } catch {
    return "";
  }
}

function getStoredUserId() {
  const direct =
    localStorage.getItem("userId") ||
    localStorage.getItem("user_id") ||
    localStorage.getItem("uid") ||
    localStorage.getItem("id");
  if (direct) return direct;

  const objectKeys = ["user", "userData", "userInfo", "currentUser", "profile"];
  for (const key of objectKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const id =
        parsed?._id ||
        parsed?.id ||
        parsed?.userId ||
        parsed?.user_id ||
        parsed?.uid;
      if (id) return String(id);
    } catch {
      // not JSON, skip
    }
  }

  const token =
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken");
  const fromJwt = decodeJwtUserId(token);
  if (fromJwt) return String(fromJwt);

  return "";
}

export default function UserMeetRoomPage() {
  const { joinCode } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [meetingData, setMeetingData] = useState(state?.meetingData ?? null);
  const [loading, setLoading] = useState(!state?.meetingData);

  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [refreshingParticipants, setRefreshingParticipants] = useState(false);

  const authToken = useMemo(() => {
    return (
      state?.authToken ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  }, [state?.authToken]);

  /* ─── Socket ID state — updates on every reconnect ─── */
  const [socketId, setSocketId] = useState(() => socketService.getSocketId());
  const myUserIdRef = useRef("");

  const { on } = useSocket(authToken);

  /* ─── Track socket reconnects via socketId state ─── */
  useEffect(() => {
    const handleConnect = () => {
      const newId = socketService.getSocketId();
      setSocketId(newId);
      console.log("✅ socketId updated:", newId);
    };

    socketService.on("connect", handleConnect);
    return () => socketService.off("connect", handleConnect);
  }, []);

  /* ─── Breakout State ─── */
  const [showCreateBreakout, setShowCreateBreakout] = useState(false);
  const [creatingBreakout, setCreatingBreakout] = useState(false);

  const [inBreakout, setInBreakout] = useState(false);
  const [breakoutData, setBreakoutData] = useState(null);
  const [isBreakoutOwner, setIsBreakoutOwner] = useState(false);

  const [breakoutInvite, setBreakoutInvite] = useState(null);
  const [respondingToInvite, setRespondingToInvite] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitingToBreakout, setInvitingToBreakout] = useState(false);
  const [alreadyInvitedUsers, setAlreadyInvitedUsers] = useState([]);

  const [showEndBreakoutModal, setShowEndBreakoutModal] = useState(false);
  const [endingBreakout, setEndingBreakout] = useState(false);

  const [showActiveBreakoutModal, setShowActiveBreakoutModal] = useState(false);
  const [rejoiningBreakout, setRejoiningBreakout] = useState(false);
  const [endingFromActiveModal, setEndingFromActiveModal] = useState(false);

  /* ─── Waiting Room / Admission State (host only) ─── */
  const [admissionRequests, setAdmissionRequests] = useState([]);
  const [processingAdmissionIds, setProcessingAdmissionIds] = useState(
    new Set(),
  );

  /* ─── Main Meeting State ─── */
  const meetingId = meetingData?.meetingId ?? null;
  const livekitServerUrl = meetingData?.livekitServerUrl ?? "";
  const livekitToken = meetingData?.livekitToken ?? "";
  const title = meetingData?.title ?? "Meeting";
  const isCreator = meetingData?.isCreator ?? false;
  const capacity = meetingData?.capacity ?? 8;

  /* ─── Resolve and cache userId as soon as meetingData is available ─── */
  useEffect(() => {
    if (!meetingData) return;

    const fromMeetingData =
      meetingData?.userId ||
      meetingData?.user?.id ||
      meetingData?.user?._id ||
      meetingData?.participantUserId ||
      "";

    const resolved = fromMeetingData
      ? String(fromMeetingData)
      : getStoredUserId();

    myUserIdRef.current = resolved;
    console.log(`✅ Resolved userId: ${resolved}`);

    if (resolved && !localStorage.getItem("userId")) {
      localStorage.setItem("userId", resolved);
    }
  }, [meetingData]);

  const permissions = {
    canPublishAudio: true,
    canPublishVideo: true,
    canPublishScreen: true,
    canPublishData: true,
  };

  const initialMicEnabled = state?.initialMicEnabled ?? true;
  const initialCameraEnabled = state?.initialCameraEnabled ?? true;

  const { deviceStatus, setDeviceStatus } = useDeviceCheck({
    initialMicEnabled,
    initialCameraEnabled,
    canPublishAudio: permissions.canPublishAudio,
    canPublishVideo: permissions.canPublishVideo,
  });

  const audio = deviceStatus.audioAllowed && permissions.canPublishAudio;
  const video = deviceStatus.videoAllowed && permissions.canPublishVideo;

  const [connect, setConnect] = useState(true);
  const [uiError, setUiError] = useState("");
  const [connected, setConnected] = useState(false);
  const [mainMeetingEnded, setMainMeetingEnded] = useState(false);

  const serverUrl = useMemo(() => {
    if (!livekitServerUrl) return "";
    const isHttps = window.location.protocol === "https:";
    let url = livekitServerUrl;
    if (isHttps) {
      if (url.startsWith("ws://")) url = url.replace(/^ws:\/\//, "wss://");
      else if (url.startsWith("http://"))
        url = url.replace(/^http:\/\//, "https://");
    }
    return url;
  }, [livekitServerUrl]);

  /* ─── Fetch meeting participants ─── */
  const fetchMeetingParticipants = useCallback(async () => {
    if (!joinCode) return;
    try {
      setRefreshingParticipants(true);
      const res = await getMeetingDetails(joinCode);
      if (res?.success && res?.data?.participants) {
        const myUserId = myUserIdRef.current;
        const participants = res.data.participants
          .filter(
            (p) =>
              p.participantStatus === "active" &&
              String(p.userId) !== String(myUserId),
          )
          .map((p) => ({
            userId: p.userId,
            id: p.userId,
            name: p.name,
            email: p.email,
            image: p.image,
          }));
        setMeetingParticipants(participants);
      }
    } catch (err) {
      console.error("Failed to fetch meeting participants:", err?.message);
    } finally {
      setRefreshingParticipants(false);
    }
  }, [joinCode]);

  /* ─── Fetch meeting data if not passed via state ─── */
  useEffect(() => {
    if (meetingData || !joinCode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await joinMeeting(joinCode);
        if (cancelled) return;
        if (!res?.success)
          throw new Error(res?.message || "Failed to join meeting");
        setMeetingData(res.data);
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.message || "Failed to join meeting");
        navigate("/user-meet", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [joinCode, meetingData, navigate]);

  /* ─── Fetch participants when connected ─── */
  useEffect(() => {
    if (connected && joinCode) {
      fetchMeetingParticipants();
    }
  }, [connected, joinCode, fetchMeetingParticipants]);

  /* ─── Page title ─── */
  useEffect(() => {
    document.title = `${title} | UserMeet`;
  }, [title]);

  /* ─── Validate meeting data ─── */
  useEffect(() => {
    if (!meetingId || !serverUrl || !livekitToken) {
      if (!loading) {
        setUiError(
          "Missing meeting details. Please go back and join the meeting again.",
        );
      }
    } else {
      setUiError("");
    }
  }, [meetingId, serverUrl, livekitToken, loading]);

  /* ─── Register socket rooms — re-runs on every reconnect via socketId ─── */
  useEffect(() => {
    if (!meetingId || !socketId || !socketService.isConnected()) return;

    const myUserId = myUserIdRef.current || getStoredUserId();

    if (!myUserIdRef.current && myUserId) {
      myUserIdRef.current = myUserId;
    }

    if (!myUserId) {
      console.error("❌ Cannot register socket rooms — userId empty");
      return;
    }

    socketService.emit("register-usermeet-user", {
      userId: myUserId,
      meetingId,
    });
    socketService.emit("register-usermeet-waiting-user", {
      userId: myUserId,
      meetingId,
    });
    console.log(`✅ Registered | userId: ${myUserId} | socketId: ${socketId}`);
  }, [meetingId, socketId]);

  /* ─── Listen for meeting ended ─── */
  useEffect(() => {
    if (!meetingId) return;
    const cleanup = on("usermeet-ended", (payload) => {
      if (String(payload?.meetingId) !== String(meetingId)) return;
      toast.error("This meeting has been ended by the host.");
      setMainMeetingEnded(true);
      setConnect(false);
      setTimeout(() => navigate("/user-meet", { replace: true }), 500);
    });
    return cleanup;
  }, [on, meetingId, navigate]);

  /* ─── Listen for participant join/leave ─── */
  useEffect(() => {
    if (!meetingId) return;

    // Helper: get a display name from the socket payload, falling back to the
    // local participants cache when the server didn't include userName.
    const resolveName = (payload) => {
      if (payload?.userName) return payload.userName;
      if (payload?.name) return payload.name;
      // Look up by userId in the cached participants list
      const userId = payload?.userId || payload?.participantUserId;
      if (userId) {
        const found = meetingParticipants.find(
          (p) => String(p.userId || p.id) === String(userId),
        );
        if (found?.name) return found.name;
      }
      return "You";
    };

    const cleanupJoin = on("usermeet-participant-joined", (payload) => {
      if (String(payload?.meetingId) !== String(meetingId)) return;
      toast.success(`${resolveName(payload)} joined the meeting`);
      fetchMeetingParticipants();
    });
    const cleanupLeave = on("usermeet-participant-left", (payload) => {
      if (String(payload?.meetingId) !== String(meetingId)) return;
      toast.info(`${resolveName(payload)} left the meeting`);
      fetchMeetingParticipants();
    });
    return () => {
      cleanupJoin();
      cleanupLeave();
    };
  }, [on, meetingId, fetchMeetingParticipants, meetingParticipants]);

  /* ─── WAITING ROOM: Listen for admission requests (host only) ─── */
  useEffect(() => {
    if (!meetingId || !isCreator) return;

    const handleAdmissionRequest = (payload) => {
      console.log("🔔 RAW admission request payload:", JSON.stringify(payload));

      if (String(payload?.meetingId) !== String(meetingId)) return;

      setAdmissionRequests((prev) => {
        const exists = prev.some(
          (r) => r.participantId === payload.participantId,
        );
        if (exists) return prev;

        toast.info(
          `${payload.userName || payload.name || "Someone"} is waiting to join`,
          { duration: 4000, icon: "🔔" },
        );

        return [
          ...prev,
          {
            participantId: payload.participantId,
            userId: payload.userId,
            userName: payload.userName || payload.name || "Unknown",
            userEmail: payload.userEmail || payload.email || "",
            userImage: payload.userImage || payload.image || null,
            meetingId: payload.meetingId,
            requestedAt: new Date(),
          },
        ];
      });
    };

    // ✅ Listen for both hyphen and underscore variants
    socketService.on("usermeet-admission-request", handleAdmissionRequest);
    socketService.on("usermeet_admission_request", handleAdmissionRequest);
    console.log(
      "✅ Host listening for admission requests | socketId:",
      socketService.getSocketId(),
    );

    return () => {
      socketService.off("usermeet-admission-request", handleAdmissionRequest);
      socketService.off("usermeet_admission_request", handleAdmissionRequest);
    };
  }, [meetingId, isCreator]);

  /* ─── BREAKOUT: Listen for invite ─── */
  useEffect(() => {
    if (!meetingId) return;
    const cleanup = on("usermeet-breakout-invite", (payload) => {
      if (String(payload?.meetingId) !== String(meetingId)) return;
      setBreakoutInvite(payload);
    });
    return cleanup;
  }, [on, meetingId]);

  /* ─── BREAKOUT: Listen for breakout ended ─── */
  useEffect(() => {
    if (!meetingId) return;
    const cleanup = on("usermeet-breakout-ended", (payload) => {
      const matchesCurrentBreakout =
        breakoutData &&
        String(payload?.breakoutId) === String(breakoutData?.breakoutId);
      if (!matchesCurrentBreakout) return;
      toast.info("The breakout room has ended.");
      setInBreakout(false);
      setBreakoutData(null);
      setIsBreakoutOwner(false);
      setAlreadyInvitedUsers([]);
      setShowActiveBreakoutModal(false);
      setShowEndBreakoutModal(false);
    });
    return cleanup;
  }, [on, meetingId, breakoutData]);

  /* ─── BREAKOUT: Listen for time warning ─── */
  useEffect(() => {
    if (!meetingId || !inBreakout) return;
    const cleanup = on("breakout-time-warning", (payload) => {
      if (String(payload?.breakoutId) === String(breakoutData?.breakoutId)) {
        toast.warning(
          `Your breakout room ends in ${payload?.minutesRemaining || 2} minute${
            payload?.minutesRemaining === 1 ? "" : "s"
          }.`,
          { icon: "⏰" },
        );
      }
    });
    return cleanup;
  }, [on, meetingId, inBreakout, breakoutData]);

  /* ─── Leave logic ─── */
  const wasConnectedRef = useRef(false);
  const didLeaveRef = useRef(false);
  const didNavigateRef = useRef(false);
  const connectionTimeoutRef = useRef(null);

  const leaveOnce = useCallback(async () => {
    if (didLeaveRef.current) return;
    if (!meetingId) return;
    if (!wasConnectedRef.current) return;
    didLeaveRef.current = true;
    try {
      await leaveMeeting(meetingId);
    } catch (err) {
      console.error("Failed to leave meeting:", err?.message);
    }
  }, [meetingId]);

  /* ─── Browser close ─── */
  useEffect(() => {
    if (!meetingId) return;
    const onBeforeUnload = (e) => {
      if (didLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    const onPageHide = () => {
      if (didLeaveRef.current) return;
      didLeaveRef.current = true;
      navigator.sendBeacon(
        `${import.meta.env.VITE_API_BASE_URL}/meet/${meetingId}/leave`,
        JSON.stringify({}),
      );
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [meetingId]);

  /* ─── Connection timeout ─── */
  useEffect(() => {
    if (!connect) return;
    connectionTimeoutRef.current = setTimeout(() => {
      if (!wasConnectedRef.current) {
        setUiError(
          "Unable to connect to the meeting server. Please check your connection and try again.",
        );
        setConnect(false);
      }
    }, 15000);
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };
  }, [connect]);

  /* ─── Room callbacks ─── */
  const handleRoomConnected = useCallback(() => {
    wasConnectedRef.current = true;
    setConnected(true);
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    toast.success("Connected to meeting");
  }, []);

  const handleEndCall = useCallback(async () => {
    if (inBreakout) setConnected(false);
    setConnect(false);
    await leaveOnce();
    if (!didNavigateRef.current) {
      didNavigateRef.current = true;
      navigate("/user-meet", { replace: true });
    }
  }, [leaveOnce, navigate, inBreakout]);

  const handleEndMeeting = useCallback(async () => {
    if (!isCreator) {
      toast.error("Only the meeting host can end the meeting for everyone.");
      return;
    }
    if (!confirm("Are you sure you want to end this meeting for everyone?"))
      return;
    try {
      setMainMeetingEnded(true);
      await endMeeting(meetingId);
      toast.success("Meeting ended successfully.");
      setConnect(false);
      setTimeout(() => navigate("/user-meet", { replace: true }), 300);
    } catch (err) {
      setMainMeetingEnded(false);
      toast.error(err?.message || "Failed to end meeting. Please try again.");
    }
  }, [isCreator, meetingId, navigate]);

  const handleRoomDisconnected = useCallback(async () => {
    setConnected(false);
    await leaveOnce();
    if (!didNavigateRef.current) {
      didNavigateRef.current = true;
      navigate("/user-meet", { replace: true });
    }
  }, [leaveOnce, navigate]);

  const handleRoomError = useCallback((e) => {
    if (e?.message?.includes("Client initiated disconnect")) return;
    const isSSLError =
      e?.message?.includes("SSL") ||
      e?.message?.includes("certificate") ||
      e?.message?.includes("ERR_CERT") ||
      e?.message?.includes("secure connection") ||
      e?.message?.includes("Failed to fetch");
    if (isSSLError) {
      setUiError(
        "Unable to establish a secure connection to the meeting server. Please contact support.",
      );
    } else {
      setUiError(
        "An error occurred while connecting to the meeting. Please try rejoining.",
      );
    }
  }, []);

  /* ─── ADMISSION: Admit participant ─── */
  const handleAdmit = useCallback(
    async (request) => {
      if (!request?.participantId) return;
      try {
        setProcessingAdmissionIds(
          (prev) => new Set([...prev, request.participantId]),
        );
        await admitParticipant(meetingId, request.participantId);
        setAdmissionRequests((prev) =>
          prev.filter((r) => r.participantId !== request.participantId),
        );
        toast.success(
          `${request.userName || "Participant"} has been admitted.`,
        );
      } catch (err) {
        toast.error(err?.message || "Failed to admit participant.");
      } finally {
        setProcessingAdmissionIds((prev) => {
          const next = new Set(prev);
          next.delete(request.participantId);
          return next;
        });
      }
    },
    [meetingId],
  );

  /* ─── ADMISSION: Deny participant ─── */
  const handleDeny = useCallback(
    async (request) => {
      if (!request?.participantId) return;
      try {
        setProcessingAdmissionIds(
          (prev) => new Set([...prev, request.participantId]),
        );
        await denyParticipant(meetingId, request.participantId);
        setAdmissionRequests((prev) =>
          prev.filter((r) => r.participantId !== request.participantId),
        );
        toast.info(`${request.userName || "Participant"} was denied entry.`);
      } catch (err) {
        toast.error(err?.message || "Failed to deny participant.");
      } finally {
        setProcessingAdmissionIds((prev) => {
          const next = new Set(prev);
          next.delete(request.participantId);
          return next;
        });
      }
    },
    [meetingId],
  );

  /* ─── ADMISSION: Admit all ─── */
  const handleAdmitAll = useCallback(async () => {
    const currentRequests = [...admissionRequests];
    for (const request of currentRequests) {
      await handleAdmit(request);
    }
  }, [admissionRequests, handleAdmit]);

  /* ─── ADMISSION: Deny all ─── */
  const handleDenyAll = useCallback(async () => {
    const currentRequests = [...admissionRequests];
    for (const request of currentRequests) {
      await handleDeny(request);
    }
  }, [admissionRequests, handleDeny]);

  /* ─── BREAKOUT: Create ─── */
  const handleCreateBreakout = useCallback(
    async ({ timeLimitMinutes }) => {
      try {
        setCreatingBreakout(true);
        const res = await createBreakout(meetingId, { timeLimitMinutes });
        if (!res?.success)
          throw new Error(res?.message || "Failed to create breakout room.");
        const data = res.data;
        setBreakoutData({
          breakoutId: data.breakoutId,
          livekitRoomName: data.livekitRoomName,
          livekitToken: data.livekitToken,
          livekitServerUrl: data.livekitServerUrl,
          timeLimitMinutes: data.timeLimitMinutes,
          expiresAt: data.expiresAt,
          meetingId: meetingId,
        });
        setIsBreakoutOwner(true);
        setInBreakout(true);
        setShowCreateBreakout(false);
        toast.success("Breakout room created! Invite participants now.");
        await fetchMeetingParticipants();
        setTimeout(() => setShowInviteModal(true), 500);
      } catch (err) {
        toast.error(
          err?.message || "Failed to create breakout room. Please try again.",
        );
      } finally {
        setCreatingBreakout(false);
      }
    },
    [meetingId, fetchMeetingParticipants],
  );

  /* ─── BREAKOUT: Accept invite ─── */
  const handleAcceptInvite = useCallback(async () => {
    if (!breakoutInvite) return;
    try {
      setRespondingToInvite(true);
      const res = await respondToBreakoutInvite(
        meetingId,
        breakoutInvite.breakoutId,
        true,
      );
      if (!res?.success)
        throw new Error(res?.message || "Failed to join breakout room.");
      const data = res.data;
      setBreakoutData({
        breakoutId: data.breakoutId,
        livekitRoomName: data.livekitRoomName,
        livekitToken: data.livekitToken,
        livekitServerUrl: data.livekitServerUrl,
        timeLimitMinutes: data.timeLimitMinutes,
        expiresAt: data.expiresAt,
        meetingId: meetingId,
      });
      setIsBreakoutOwner(false);
      setInBreakout(true);
      setBreakoutInvite(null);
      toast.success("Joined breakout room!");
    } catch (err) {
      toast.error(
        err?.message || "Failed to join breakout room. Please try again.",
      );
    } finally {
      setRespondingToInvite(false);
    }
  }, [meetingId, breakoutInvite]);

  /* ─── BREAKOUT: Reject invite ─── */
  const handleRejectInvite = useCallback(async () => {
    if (!breakoutInvite) return;
    try {
      setRespondingToInvite(true);
      await respondToBreakoutInvite(
        meetingId,
        breakoutInvite.breakoutId,
        false,
      );
      setBreakoutInvite(null);
      toast.info("Invitation declined.");
    } catch (err) {
      toast.error(
        err?.message || "Failed to decline the invitation. Please try again.",
      );
    } finally {
      setRespondingToInvite(false);
    }
  }, [meetingId, breakoutInvite]);

  /* ─── BREAKOUT: Invite users ─── */
  const handleInviteUsers = useCallback(
    async (userIds) => {
      if (!breakoutData?.breakoutId) return;
      try {
        setInvitingToBreakout(true);
        const res = await inviteToBreakout(
          meetingId,
          breakoutData.breakoutId,
          userIds,
        );
        if (!res?.success)
          throw new Error(res?.message || "Failed to send invitations.");
        setAlreadyInvitedUsers((prev) => [...prev, ...userIds]);
        setShowInviteModal(false);
        toast.success(
          `Invited ${userIds.length} participant${userIds.length !== 1 ? "s" : ""} successfully.`,
        );
      } catch (err) {
        toast.error(
          err?.message || "Failed to send invitations. Please try again.",
        );
      } finally {
        setInvitingToBreakout(false);
      }
    },
    [meetingId, breakoutData],
  );

  /* ─── BREAKOUT: Leave ─── */
  const handleLeaveBreakout = useCallback(async () => {
    if (!breakoutData?.breakoutId) return;
    try {
      await leaveBreakout(meetingId, breakoutData.breakoutId);
    } catch (err) {
      console.error("leaveBreakout error:", err?.message);
    } finally {
      setInBreakout(false);
      toast.info("Returned to main meeting.");
    }
  }, [meetingId, breakoutData]);

  /* ─── BREAKOUT: Request end ─── */
  const handleRequestEndBreakout = useCallback(() => {
    if (!isBreakoutOwner || !breakoutData?.breakoutId) return;
    setShowEndBreakoutModal(true);
  }, [isBreakoutOwner, breakoutData]);

  /* ─── BREAKOUT: Confirm end ─── */
  const handleConfirmEndBreakout = useCallback(async () => {
    if (!isBreakoutOwner || !breakoutData?.breakoutId) return;
    try {
      setEndingBreakout(true);
      await endBreakout(meetingId, breakoutData.breakoutId);
      toast.success("Breakout room ended successfully.");
    } catch (err) {
      toast.error(
        err?.message || "Failed to end the breakout room. Please try again.",
      );
      return;
    } finally {
      setEndingBreakout(false);
    }
    setShowEndBreakoutModal(false);
    setInBreakout(false);
    setBreakoutData(null);
    setIsBreakoutOwner(false);
    setAlreadyInvitedUsers([]);
  }, [meetingId, breakoutData, isBreakoutOwner]);

  /* ─── BREAKOUT: Button click ─── */
  const handleBreakoutButtonClick = useCallback(() => {
    if (breakoutData?.breakoutId) {
      if (breakoutData.expiresAt) {
        const now = new Date();
        const expiry = new Date(breakoutData.expiresAt);
        if (now >= expiry) {
          setBreakoutData(null);
          setIsBreakoutOwner(false);
          setAlreadyInvitedUsers([]);
          setShowCreateBreakout(true);
          return;
        }
      }
      setShowActiveBreakoutModal(true);
    } else {
      setShowCreateBreakout(true);
    }
  }, [breakoutData]);

  /* ─── BREAKOUT: Rejoin ─── */
  const handleRejoinBreakout = useCallback(async () => {
    if (!breakoutData?.breakoutId) return;
    try {
      setRejoiningBreakout(true);
      const res = await rejoinBreakout(meetingId, breakoutData.breakoutId);
      if (!res?.success)
        throw new Error(res?.message || "Failed to rejoin breakout room.");
      const data = res.data;
      setBreakoutData((prev) => ({
        ...prev,
        livekitToken: data.livekitToken,
        livekitServerUrl: data.livekitServerUrl || prev.livekitServerUrl,
        expiresAt: data.expiresAt || prev.expiresAt,
      }));
      setShowActiveBreakoutModal(false);
      setInBreakout(true);
      toast.success("Rejoined breakout room!");
    } catch (err) {
      toast.error(
        err?.message ||
          "Unable to rejoin the breakout room. It may have ended.",
      );
      setBreakoutData(null);
      setIsBreakoutOwner(false);
      setAlreadyInvitedUsers([]);
      setShowActiveBreakoutModal(false);
    } finally {
      setRejoiningBreakout(false);
    }
  }, [meetingId, breakoutData]);

  /* ─── BREAKOUT: End and create new ─── */
  const handleEndAndCreateNew = useCallback(async () => {
    if (!breakoutData?.breakoutId) return;
    try {
      setEndingFromActiveModal(true);
      await endBreakout(meetingId, breakoutData.breakoutId);
      toast.success("Breakout room ended.");
    } catch (err) {
      toast.error(
        err?.message || "Failed to end the breakout room. Please try again.",
      );
      return;
    } finally {
      setEndingFromActiveModal(false);
    }
    setShowActiveBreakoutModal(false);
    setInBreakout(false);
    setBreakoutData(null);
    setIsBreakoutOwner(false);
    setAlreadyInvitedUsers([]);
    setTimeout(() => setShowCreateBreakout(true), 300);
  }, [meetingId, breakoutData]);

  /* ─── Handle opening invite modal ─── */
  const handleOpenInviteModal = useCallback(async () => {
    await fetchMeetingParticipants();
    setShowInviteModal(true);
  }, [fetchMeetingParticipants]);

  /* ─── Loading/Error States ─── */
  if (loading || deviceStatus.checking) {
    return (
      <div className="jt-error">
        <div className="jt-errorCard">
          <div className="jt-spinner" style={{ margin: "0 auto 20px" }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
          <h2>Preparing meeting...</h2>
          <p>
            {loading
              ? "Loading meeting details..."
              : "Checking camera and microphone..."}
          </p>
        </div>
      </div>
    );
  }

  if (uiError) {
    const isSSLError =
      uiError.includes("secure connection") ||
      uiError.includes("SSL") ||
      uiError.includes("certificate");
    return (
      <div className="jt-error">
        <div className="jt-errorCard">
          <h2>Unable to join meeting</h2>
          <p>{uiError}</p>
          {isSSLError && (
            <div className="jt-sslWarn">
              <strong>SSL Certificate Issue Detected</strong>
              <p>
                The meeting server has an invalid or missing SSL certificate.
                Please contact support.
              </p>
            </div>
          )}
          <button
            className="jt-primaryBtn"
            onClick={async () => {
              await leaveOnce();
              navigate("/user-meet");
            }}
            type="button"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  /* ─── Main Render ─── */
  return (
    <div className="meeting-room-container jt-container">
      <LiveKitRoom
        key={`usermeet-${meetingId}`}
        serverUrl={serverUrl}
        token={livekitToken}
        connect={connect}
        audio={!inBreakout && audio}
        video={!inBreakout && video}
        data-lk-theme="default"
        options={{ adaptiveStream: true, dynacast: true }}
        onConnected={handleRoomConnected}
        onDisconnected={handleRoomDisconnected}
        onError={handleRoomError}
        className="livekit-room-custom jt-livekit"
        style={{ opacity: inBreakout ? 0.3 : 1 }}
      >
        <RoomAudioRenderer />

        <UserMeetStage
          meetingId={meetingId}
          meetingTitle={title}
          joinCode={joinCode}
          isCreator={isCreator}
          connected={connected}
          permissions={permissions}
          onLeave={handleEndCall}
          onEndMeeting={handleEndMeeting}
          deviceStatus={deviceStatus}
          setDeviceStatus={setDeviceStatus}
          capacity={capacity}
          authToken={authToken}
          isBreakout={false}
          isBreakoutOwner={false}
          onCreateBreakout={handleBreakoutButtonClick}
          onInviteToBreakout={null}
        />
      </LiveKitRoom>

      {/* Breakout Overlay */}
      {inBreakout && breakoutData && (
        <BreakoutOverlay
          breakoutData={breakoutData}
          onLeave={handleLeaveBreakout}
          onEnd={handleRequestEndBreakout}
          onInvite={handleOpenInviteModal}
          isOwner={isBreakoutOwner}
          authToken={authToken}
          deviceStatus={deviceStatus}
          setDeviceStatus={setDeviceStatus}
          mainMeetingConnected={connected}
          mainMeetingEnded={mainMeetingEnded}
        />
      )}

      {/* WAITING ROOM: Admission requests panel (host only) */}
      {isCreator && admissionRequests.length > 0 && (
        <AdmissionRequestModal
          requests={admissionRequests}
          onAdmit={handleAdmit}
          onDeny={handleDeny}
          onAdmitAll={handleAdmitAll}
          onDenyAll={handleDenyAll}
          processingIds={processingAdmissionIds}
        />
      )}

      {/* Modals */}
      <CreateBreakoutModal
        open={showCreateBreakout}
        onClose={() => setShowCreateBreakout(false)}
        onCreate={handleCreateBreakout}
        creating={creatingBreakout}
      />

      <BreakoutInviteModal
        invite={breakoutInvite}
        onAccept={handleAcceptInvite}
        onReject={handleRejectInvite}
        responding={respondingToInvite}
      />

      <InviteToBreakoutModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        participants={meetingParticipants}
        onInvite={handleInviteUsers}
        inviting={invitingToBreakout}
        alreadyInvited={alreadyInvitedUsers}
      />

      <EndBreakoutModal
        open={showEndBreakoutModal}
        onClose={() => setShowEndBreakoutModal(false)}
        onConfirm={handleConfirmEndBreakout}
        ending={endingBreakout}
      />

      <BreakoutActiveModal
        open={showActiveBreakoutModal}
        onClose={() => setShowActiveBreakoutModal(false)}
        onRejoin={handleRejoinBreakout}
        onEnd={handleEndAndCreateNew}
        breakoutData={breakoutData}
        isOwner={isBreakoutOwner}
        ending={endingFromActiveModal}
        rejoining={rejoiningBreakout}
      />
    </div>
  );
}
